import React from 'react';
import { X } from 'lucide-react';
import { type MusicTrack } from '@/services/musicTracksService';
import { TrackCover } from './TrackCover';
import { Button } from './ui/button';
import { cn } from '@/lib/utils';

function formatDuration(seconds: number): string {
  if (!seconds || isNaN(seconds)) return '--:--';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

interface GenreTopTracksPanelProps {
  category: string;
  tracks: MusicTrack[];
  onClose: () => void;
}

export function GenreTopTracksPanel({
  category,
  tracks,
  onClose,
}: GenreTopTracksPanelProps) {
  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <div className="flex shrink-0 items-center justify-between border-b px-4 py-3">
        <h3 className="font-semibold truncate">
          Top 10 — {capitalize(category)}
        </h3>
        <Button variant="ghost" size="icon" className="shrink-0" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto p-4">
        <ul className="space-y-2">
          {tracks.map((track, index) => (
            <li
              key={track.id}
              className={cn(
                'flex items-center gap-3 rounded-lg border p-3 transition-colors',
                'hover:bg-muted/50'
              )}
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold tabular-nums">
                {index + 1}
              </span>
              <TrackCover coverPath={track.coverPath} size="sm" />
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate">{track.title || 'Untitled'}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {track.artist || 'Unknown artist'}
                </p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-0.5">
                <span className="text-sm font-medium tabular-nums">
                  {track.playCount ?? 0} plays
                </span>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {formatDuration(track.duration)}
                </span>
              </div>
            </li>
          ))}
        </ul>
        {tracks.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No tracks with plays in this genre
          </p>
        )}
      </div>
    </div>
  );
}
